import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { Download, FileText, FolderPlus, Image as ImageIcon, PenLine, RefreshCw, ShieldCheck, Sparkles, Trash2, Upload } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { api, del, get, patch, post, put } from '../lib/api.ts'
import { fmtBytes, fmtDate, fmtDateTime } from '../lib/format.ts'
import { useCondo } from '../lib/hooks.ts'
import { CATEGORY_OPTIONS, VISIBILITY_LABEL, type DocRow, type Visibility } from '../lib/types.ts'
import { Badge, Button, Dialog, Empty, ErrorNote, Field, Input, PageHeader, Select, Sheet, Spinner, Textarea } from '../components/ui.tsx'
import { Markdown } from '../components/Markdown.tsx'
import { SignaturesPanel } from '../components/Signatures.tsx'
import { t } from '../lib/i18n.ts'

const statusBadge = (s?: string) =>
  s === 'done' ? null : s === 'failed' ? <Badge tone="margem">{t('Falhou a leitura')}</Badge> : <Badge tone="alerta">{t('Lendo o documento…')}</Badge>

const visTone = (v: Visibility) => (v === 'todos' ? 'neutral' : v === 'conselho' ? 'caneta' : 'margem') as 'neutral' | 'caneta' | 'margem'

export function Acervo() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { condoId, can, info } = useCondo(slug)
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [category, setCategory] = useState('')
  const [folder, setFolder] = useState('')
  const [uploading, setUploading] = useState(false)
  const [newFolder, setNewFolder] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(q), 300)
    return () => clearTimeout(timer)
  }, [q])
  const params = new URLSearchParams({ ...(debounced ? { q: debounced } : {}), ...(category ? { category } : {}), ...(folder ? { folderId: folder } : {}) })
  const docs = useQuery({
    queryKey: ['docs', condoId, params.toString()],
    queryFn: () => get<{ documents: DocRow[] }>(`/api/condos/${condoId}/documents?${params}`),
    enabled: Boolean(condoId),
    refetchInterval: (qq) => (qq.state.data?.documents.some((d) => d.current && d.current.status !== 'done' && d.current.status !== 'failed') ? 4000 : false),
  })
  const folders = useQuery({ queryKey: ['folders', condoId], queryFn: () => get<{ folders: { id: string; name: string }[] }>(`/api/condos/${condoId}/folders`), enabled: Boolean(condoId) })
  const role = info.data?.me.role

  return (
    <div>
      <PageHeader title={t('Acervo')}>
        {can('documents.manage') && (
          <Button variant="outline" onClick={() => setNewFolder(true)}>
            <FolderPlus className="size-5" />{' '}{t('Nova pasta')}
          </Button>
        )}
        {can('documents.upload') && (
          <Button onClick={() => setUploading(true)}>
            <Upload className="size-5" />{' '}{t('Enviar documento')}
          </Button>
        )}
      </PageHeader>
      <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_12rem_12rem]">
        <Input type="search" placeholder={t('Buscar por título ou conteúdo')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('Buscar')} />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label={t('Tipo')}>
          <option value="">{t('Todos os tipos')}</option>
          {CATEGORY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        <Select value={folder} onChange={(e) => setFolder(e.target.value)} aria-label={t('Pasta')}>
          <option value="">{t('Todas as pastas')}</option>
          <option value="none">{t('Sem pasta')}</option>
          {folders.data?.folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </Select>
      </div>
      {docs.isLoading ? (
        <Spinner />
      ) : docs.data!.documents.length === 0 ? (
        <Empty
          title={debounced || category || folder ? t('Nada encontrado') : t('O acervo está vazio')}
          action={can('documents.upload') && !debounced ? <Button onClick={() => setUploading(true)}><Upload className="size-5" />{' '}{t('Enviar o primeiro documento')}</Button> : undefined}
        >
          {debounced || category || folder
            ? t('Tente outras palavras ou limpe os filtros.')
            : can('documents.upload')
              ? t('Comece pela convenção e pelo regimento interno. Documentos digitalizados (fotos e PDFs escaneados) também funcionam: o texto é lido automaticamente.')
              : t('Quando o síndico enviar a convenção, o regimento e as atas, eles aparecem aqui.')}
        </Empty>
      ) : (
        <Sheet>
          {docs.data!.documents.map((d) => (
            <Link key={d.id} to="/c/$slug/acervo/$docId" params={{ slug, docId: d.id }} className="flex items-start gap-3 border-b border-pauta px-4 py-3.5 last:border-b-0 hover:bg-papel">
              {d.current?.mime.startsWith('image/') ? <ImageIcon className="mt-0.5 size-5 shrink-0 text-tinta-3" /> : <FileText className="mt-0.5 size-5 shrink-0 text-tinta-3" />}
              <div className="min-w-0 flex-1">
                <p className="font-semibold leading-snug">{d.title}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-tinta-2">
                  <span>{d.categoryLabel}</span>
                  <span>{fmtDate(d.docDate ?? d.createdAt)}</span>
                  {d.current?.pages ? <span>{d.current.pages} {d.current.pages === 1 ? t('página') : t('páginas')}</span> : null}
                  {statusBadge(d.current?.status)}
                  {d.current?.signature === 'present' && <Badge tone="carimbo"><ShieldCheck className="size-3.5" />{' '}{t('Assinado')}</Badge>}
                  {d.current?.immutable && <Badge tone="carimbo">{t('Finalizado')}</Badge>}
                  {d.visibility !== 'todos' && <Badge tone={visTone(d.visibility)}>{d.visibility === 'conselho' ? t('Conselho') : t('Só síndico')}</Badge>}
                </p>
              </div>
            </Link>
          ))}
        </Sheet>
      )}
      <UploadDialog open={uploading} onClose={() => setUploading(false)} condoId={condoId} slug={slug} role={role} folders={folders.data?.folders ?? []} />
      <NewFolderDialog open={newFolder} onClose={() => setNewFolder(false)} condoId={condoId} />
    </div>
  )
}

function visibilityOptions(role?: string): Visibility[] {
  if (role === 'sindico' || role === 'subsindico') return ['todos', 'conselho', 'sindico']
  if (role === 'conselheiro' || role === 'administradora') return ['todos', 'conselho']
  return ['todos']
}

function UploadDialog({ open, onClose, condoId, slug, role, folders }: { open: boolean; onClose: () => void; condoId: string; slug: string; role?: string; folders: { id: string; name: string }[] }) {
  const qc = useQueryClient()
  const nav = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('convencao')
  const [visibility, setVisibility] = useState<Visibility>('todos')
  const [docDate, setDocDate] = useState('')
  const [folderId, setFolderId] = useState('')
  const m = useMutation({
    mutationFn: () => {
      const fd = new FormData()
      fd.set('file', file!)
      if (title) fd.set('title', title)
      fd.set('category', category)
      fd.set('visibility', visibility)
      if (docDate) fd.set('docDate', docDate)
      if (folderId) fd.set('folderId', folderId)
      return api<{ id: string }>('POST', `/api/condos/${condoId}/documents`, fd)
    },
    onSuccess: (r) => {
      toast.success(t('Documento enviado. Estamos lendo o conteúdo.'))
      qc.invalidateQueries({ queryKey: ['docs', condoId] })
      onClose()
      setFile(null)
      setTitle('')
      nav({ to: '/c/$slug/acervo/$docId', params: { slug, docId: r.id } })
    },
  })
  return (
    <Dialog open={open} onClose={onClose} title={t('Enviar documento')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (file) m.mutate() }}>
        <Field label={t('Arquivo')} htmlFor="up-file" hint={t('PDF (inclusive escaneado), foto (JPG, PNG, WEBP), DOCX ou texto. Até 50 MB.')}>
          <label className="flex min-h-14 cursor-pointer items-center gap-3 rounded-ficha border border-dashed border-caneta/50 bg-caneta-clara/40 px-3 py-2 hover:border-caneta">
            <Upload className="size-5 shrink-0 text-caneta" />
            <span className="min-w-0 flex-1 truncate">{file ? <><strong>{file.name}</strong> <span className="text-tinta-2">({fmtBytes(file.size)})</span></> : <span className="font-semibold text-caneta">{t('Escolher arquivo')}</span>}</span>
            <input id="up-file" type="file" required accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.txt,.md" className="sr-only" onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ')) }} />
          </label>
        </Field>
        <Field label={t('Título')} htmlFor="up-title">
          <Input id="up-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={t('Ex.: Convenção do Condomínio')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('Tipo')} htmlFor="up-cat">
            <Select id="up-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>
          <Field label={t('Data do documento')} htmlFor="up-date" hint={t('Ex.: data da assembleia.')}>
            <Input id="up-date" type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} />
          </Field>
        </div>
        <Field label={t('Quem pode ver')} htmlFor="up-vis" hint={t('O assistente só usa o documento para quem pode vê-lo.')}>
          <Select id="up-vis" value={visibility} onChange={(e) => setVisibility(e.target.value as Visibility)}>
            {visibilityOptions(role).map((v) => <option key={v} value={v}>{VISIBILITY_LABEL[v]}</option>)}
          </Select>
        </Field>
        {folders.length > 0 && (
          <Field label={t('Pasta')} htmlFor="up-folder">
            <Select id="up-folder" value={folderId} onChange={(e) => setFolderId(e.target.value)}>
              <option value="">{t('Sem pasta')}</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </Select>
          </Field>
        )}
        <ErrorNote error={m.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="quiet" onClick={onClose}>{t('Cancelar')}</Button>
          <Button type="submit" busy={m.isPending} disabled={!file}>{t('Enviar')}</Button>
        </div>
      </form>
    </Dialog>
  )
}

function NewFolderDialog({ open, onClose, condoId }: { open: boolean; onClose: () => void; condoId: string }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const m = useMutation({
    mutationFn: () => post(`/api/condos/${condoId}/folders`, { name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['folders', condoId] }); setName(''); onClose(); toast.success(t('Pasta criada')) },
  })
  return (
    <Dialog open={open} onClose={onClose} title={t('Nova pasta')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <Field label={t('Nome')} htmlFor="fname"><Input id="fname" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} placeholder={t('Ex.: Obras da garagem')} /></Field>
        <ErrorNote error={m.error} />
        <div className="flex justify-end"><Button type="submit" busy={m.isPending}>{t('Criar pasta')}</Button></div>
      </form>
    </Dialog>
  )
}

interface DocDetail {
  document: { id: string; title: string; description: string | null; category: string; categoryLabel: string; visibility: Visibility; docDate: string | null; folderId: string | null; currentVersionId: string | null; createdAt: string; summaryMd: string | null }
  versions: { id: string; version: number; kind: string; immutable: boolean; pages: number | null; status: string; error: string | null; signature: string | null; createdAt: string; mime: string; size: number; name: string; sha256: string; createdBy: string | null }[]
  pageSources: Record<string, number>
  canManage: boolean
}

const SOURCE_LABEL: Record<string, string> = { text: t('texto do PDF'), ocr: t('leitura automática (OCR)'), vision: t('leitura por IA'), manual: t('corrigido à mão') }

export function DocumentPage() {
  const { slug, docId } = useParams({ strict: false }) as { slug: string; docId: string }
  const { condoId, info } = useCondo(slug)
  const qc = useQueryClient()
  const nav = useNavigate()
  const page = Number(new URLSearchParams(location.search).get('pagina') ?? '') || null
  const [editMeta, setEditMeta] = useState(false)
  const [editPage, setEditPage] = useState<{ page: number; text: string } | null>(null)
  const [showText, setShowText] = useState(false)
  const d = useQuery({
    queryKey: ['doc', condoId, docId],
    queryFn: () => get<DocDetail>(`/api/condos/${condoId}/documents/${docId}`),
    enabled: Boolean(condoId),
    refetchInterval: (q) => (q.state.data && ['pending', 'running'].includes(q.state.data.versions[0]?.status ?? '') ? 3000 : false),
  })
  const pages = useQuery({
    queryKey: ['pages', condoId, docId, d.data?.document.currentVersionId],
    queryFn: () => get<{ pages: { page: number; text: string; source: string; confidence: number | null }[] }>(`/api/condos/${condoId}/documents/${docId}/pages`),
    enabled: Boolean(condoId && showText),
  })
  const reprocess = useMutation({ mutationFn: () => post(`/api/condos/${condoId}/documents/${docId}/reprocess`), onSuccess: () => { toast.success(t('Lendo o documento de novo')); qc.invalidateQueries({ queryKey: ['doc', condoId, docId] }) } })
  const summarize = useMutation({ mutationFn: () => post<{ summaryMd: string }>(`/api/condos/${condoId}/documents/${docId}/summary`), onSuccess: () => { toast.success(t('Resumo gerado')); qc.invalidateQueries({ queryKey: ['doc', condoId, docId] }) } })
  const remove = useMutation({ mutationFn: () => del(`/api/condos/${condoId}/documents/${docId}`), onSuccess: () => { toast.success(t('Documento excluído')); qc.invalidateQueries({ queryKey: ['docs', condoId] }); nav({ to: '/c/$slug/acervo', params: { slug } }) } })
  const savePage = useMutation({
    mutationFn: (p: { page: number; text: string }) => put(`/api/condos/${condoId}/documents/${docId}/pages/${p.page}`, { text: p.text }),
    onSuccess: () => { toast.success(t('Texto corrigido')); setEditPage(null); qc.invalidateQueries({ queryKey: ['pages', condoId, docId] }); qc.invalidateQueries({ queryKey: ['doc', condoId, docId] }) },
  })
  const fileUrl = `/api/condos/${condoId}/documents/${docId}/file`
  const cur = d.data?.versions[0]
  const viewer = useMemo(() => {
    if (!cur) return null
    if (cur.mime === 'application/pdf') return <iframe title={t('Documento')} src={`${fileUrl}${page ? `#page=${page}` : ''}`} className="h-[70dvh] w-full rounded-ficha border border-pauta bg-folha" />
    if (cur.mime.startsWith('image/')) return <img src={fileUrl} alt={d.data?.document.title ?? ''} className="max-h-[70dvh] w-auto rounded-ficha border border-pauta" />
    return null
  }, [cur, fileUrl, page, d.data?.document.title])

  if (d.isLoading) return <Spinner />
  if (d.error) return <ErrorNote error={d.error} />
  const doc = d.data!.document
  return (
    <div>
      <PageHeader title={doc.title} back={<Link to="/c/$slug/acervo" params={{ slug }} className="mb-2 inline-block text-[0.95rem] font-semibold text-caneta">{t('Acervo')}</Link>}>
        <a href={`${fileUrl}?download=1`} className="inline-flex min-h-11 items-center gap-2 rounded-ficha border border-pauta bg-folha px-4 font-semibold hover:border-caneta hover:text-caneta"><Download className="size-5" />{' '}{t('Baixar')}</a>
        {d.data!.canManage && <Button variant="outline" onClick={() => setEditMeta(true)}><PenLine className="size-5" />{' '}{t('Editar')}</Button>}
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-[0.95rem] text-tinta-2">
        <Badge>{doc.categoryLabel}</Badge>
        <Badge tone={visTone(doc.visibility)}>{VISIBILITY_LABEL[doc.visibility]}</Badge>
        {doc.docDate && <span>{t('Data:')}{' '}{fmtDate(doc.docDate)}</span>}
        {statusBadge(cur?.status)}
        {cur?.signature === 'present' && <Badge tone="carimbo"><ShieldCheck className="size-3.5" />{' '}{t('PDF com assinatura digital')}</Badge>}
      </div>
      {cur?.status === 'failed' && <div className="mb-4"><ErrorNote error={new Error(t('Não conseguimos ler este arquivo: {error}.', { error: cur.error ?? t('erro desconhecido') }))} /></div>}
      {doc.description && <p className="mb-4 max-w-prose">{doc.description}</p>}
      {cur?.signature === 'present' && (
        <p className="mb-4 max-w-prose text-[0.95rem] text-tinta-2">
          {t('Para conferir a validade da assinatura (gov.br ou ICP-Brasil), baixe o arquivo e envie no validador oficial do ITI:')}{' '}<span className="font-semibold">{t('validar.iti.gov.br')}</span>.
        </p>
      )}
      {viewer}
      {(doc.summaryMd || (d.data!.canManage && cur?.status === 'done')) && (
        <section className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-bold">{t('Resumo')}</h2>
            {d.data!.canManage && cur?.status === 'done' && (
              <Button variant="outline" size="sm" busy={summarize.isPending} onClick={() => summarize.mutate()}><Sparkles className="size-4" /> {doc.summaryMd ? t('Atualizar resumo') : t('Resumir com IA')}</Button>
            )}
          </div>
          {doc.summaryMd ? (
            <Sheet className="mt-2 p-4">
              <Markdown text={doc.summaryMd} />
              <p className="mt-2 text-sm text-tinta-2">{t('Resumo gerado por IA a partir do texto lido. Em caso de dúvida, vale o documento.')}</p>
            </Sheet>
          ) : <p className="mt-1 text-sm text-tinta-2">{t('Um resumo curto, com decisões, valores e prazos, para quem não vai ler o documento inteiro.')}</p>}
          <div className="mt-2"><ErrorNote error={summarize.error} /></div>
        </section>
      )}
      <section className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-bold">{t('Texto lido')}</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowText((v) => !v)}>{showText ? t('Esconder texto') : t('Ver texto por página')}</Button>
            {d.data!.canManage && <Button variant="quiet" size="sm" busy={reprocess.isPending} onClick={() => reprocess.mutate()}><RefreshCw className="size-4" />{' '}{t('Ler de novo')}</Button>}
          </div>
        </div>
        {Object.keys(d.data!.pageSources).length > 0 && (
          <p className="mt-1 text-sm text-tinta-2">
            {Object.entries(d.data!.pageSources).map(([s, n]) => n === 1 ? t('1 página por {source}', { source: SOURCE_LABEL[s] ?? s }) : t('{n} páginas por {source}', { n, source: SOURCE_LABEL[s] ?? s })).join('; ')}{t('. O assistente usa este texto; se algo estiver errado, corrija a página.')}
          </p>
        )}
        {showText && (pages.isLoading ? <Spinner /> : (
          <div className="mt-3 flex flex-col gap-3">
            {pages.data?.pages.map((p) => (
              <Sheet key={p.page} className={p.page === page ? 'ring-2 ring-marca' : ''}>
                <div className="flex items-center justify-between border-b border-pauta px-4 py-2 text-sm text-tinta-2">
                  <span>{t('Página')}{' '}{p.page} — {SOURCE_LABEL[p.source] ?? p.source}{p.confidence != null ? t(' (confiança {pct}%)', { pct: p.confidence }) : ''}</span>
                  {d.data!.canManage && <button className="font-semibold text-caneta" onClick={() => setEditPage({ page: p.page, text: p.text })}>{t('Corrigir')}</button>}
                </div>
                <p className="whitespace-pre-wrap px-4 py-3 font-doc text-[1rem] leading-relaxed">{p.text || <span className="text-tinta-3">{t('(página sem texto)')}</span>}</p>
              </Sheet>
            ))}
          </div>
        ))}
      </section>
      {cur && <SignaturesPanel condoId={condoId} docId={docId} canUpload={d.data!.canManage} isPdf={cur.mime === 'application/pdf'} />}
      <section className="mt-8">
        <h2 className="mb-2 text-xl font-bold">{t('Versões')}</h2>
        <Sheet>
          {d.data!.versions.map((v) => (
            <div key={v.id} className="border-b border-pauta px-4 py-3 last:border-b-0">
              <p className="font-semibold">{t('Versão')}{' '}{v.version} {v.kind === 'signed' && <Badge tone="carimbo">{t('via assinada')}</Badge>} {v.kind === 'generated' && <Badge tone="caneta">{t('gerada pelo sistema')}</Badge>}</p>
              <p className="text-sm text-tinta-2">{v.name}, {fmtBytes(v.size)}{t(', enviada em')}{' '}{fmtDateTime(v.createdAt)}{v.createdBy ? t(' por {name}', { name: v.createdBy }) : ''}</p>
              <p className="mt-1 break-all font-doc text-[0.8rem] text-tinta-3" title={t('Impressão digital SHA-256 do arquivo')}>{t('SHA-256')}{' '}{v.sha256}</p>
            </div>
          ))}
        </Sheet>
      </section>
      {d.data!.canManage && !cur?.immutable && (
        <div className="mt-8">
          <Button variant="danger" busy={remove.isPending} onClick={() => confirm(t('Excluir este documento do acervo?')) && remove.mutate()}><Trash2 className="size-5" />{' '}{t('Excluir documento')}</Button>
        </div>
      )}
      {editMeta && <EditMetaDialog condoId={condoId} doc={doc} role={info.data?.me.role} onClose={() => setEditMeta(false)} />}
      <Dialog open={Boolean(editPage)} onClose={() => setEditPage(null)} title={t('Corrigir página {n}', { n: editPage?.page ?? '' })} wide>
        {editPage && (
          <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); savePage.mutate(editPage) }}>
            <Textarea value={editPage.text} onChange={(e) => setEditPage({ ...editPage, text: e.target.value })} className="min-h-[50dvh] font-doc" />
            <ErrorNote error={savePage.error} />
            <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={() => setEditPage(null)}>{t('Cancelar')}</Button><Button type="submit" busy={savePage.isPending}>{t('Salvar correção')}</Button></div>
          </form>
        )}
      </Dialog>
    </div>
  )
}

function EditMetaDialog({ condoId, doc, role, onClose }: { condoId: string; doc: DocDetail['document']; role?: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [f, setF] = useState({ title: doc.title, category: doc.category, visibility: doc.visibility, docDate: doc.docDate ?? '', description: doc.description ?? '' })
  const m = useMutation({
    mutationFn: () => patch(`/api/condos/${condoId}/documents/${doc.id}`, { ...f, docDate: f.docDate || null, description: f.description || null }),
    onSuccess: () => { toast.success(t('Documento atualizado')); qc.invalidateQueries({ queryKey: ['doc', condoId, doc.id] }); qc.invalidateQueries({ queryKey: ['docs', condoId] }); onClose() },
  })
  return (
    <Dialog open onClose={onClose} title={t('Editar documento')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <Field label={t('Título')} htmlFor="em-t"><Input id="em-t" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required maxLength={200} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('Tipo')} htmlFor="em-c"><Select id="em-c" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{CATEGORY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
          <Field label={t('Data')} htmlFor="em-d"><Input id="em-d" type="date" value={f.docDate} onChange={(e) => setF({ ...f, docDate: e.target.value })} /></Field>
        </div>
        <Field label={t('Quem pode ver')} htmlFor="em-v"><Select id="em-v" value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value as Visibility })}>{visibilityOptions(role).map((v) => <option key={v} value={v}>{VISIBILITY_LABEL[v]}</option>)}</Select></Field>
        <Field label={t('Descrição')} htmlFor="em-desc"><Textarea id="em-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={2000} /></Field>
        <ErrorNote error={m.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={onClose}>{t('Cancelar')}</Button><Button type="submit" busy={m.isPending}>{t('Salvar')}</Button></div>
      </form>
    </Dialog>
  )
}
